import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const ABOUT_IMG = "/images/magazzino-3.webp";

function Counter({ target, suffix, label, testid }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1800;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <div className="space-y-1" ref={ref}>
      <div data-testid={testid} className="font-display text-4xl md:text-5xl font-bold text-[#0b2545]">
        {value.toLocaleString("it-IT")}
        <span className="text-2xl text-[#c59b27] ml-0.5">{suffix}</span>
      </div>
      <span className="text-xs uppercase font-mono-df tracking-wider text-[#0b2545]/70">{label}</span>
    </div>
  );
}

export default function About() {
  return (
    <section id="storia" data-testid="about-section" className="py-24 md:py-32 bg-[#faf8f5]">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-[#0b2545]/15">
            <img
              src={ABOUT_IMG}
              alt="Scaffali del magazzino Dahmani Food con prodotti alimentari"
              className="absolute inset-0 w-full h-full object-cover df-drift"
              data-testid="about-image"
            />
            <div className="absolute inset-0 flex flex-col justify-end p-8 bg-gradient-to-t from-[#06152d]/90 to-transparent">
              <span className="text-xs uppercase font-mono-df text-[#d4af37] tracking-widest">Dahmani Food Srls</span>
              <h3 className="font-display text-2xl text-white mt-1">Passione per la genuinità</h3>
              <p className="text-xs text-white/80 mt-2 leading-relaxed">Operiamo con rigore per garantire forniture continue e puntuali a ogni attività partner.</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.2, 0.7, 0, 1] }}
          className="lg:col-span-7 space-y-6"
        >
          <span className="text-xs uppercase font-mono-df tracking-widest text-[#c59b27] font-semibold">La Nostra Storia</span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-[#0b2545] font-normal tracking-tight">
            L'ingrosso halal che unisce tradizione e affidabilità.
          </h2>
          <p className="text-base text-[#0b2545]/80 leading-relaxed">
            Dahmani Food Srls nasce a Rozzano, alle porte di Milano, per portare il meglio dei prodotti alimentari halal all'ingrosso: riforniamo negozi etnici, ristoranti e rivenditori con specialità mediterranee e internazionali, garantendo freschezza, varietà e forniture puntuali.
          </p>

          <div className="grid grid-cols-3 gap-6 pt-8 border-t border-[#0b2545]/15">
            <Counter target={21} suffix="" label="Reparti Merceologici" testid="counter-categorie" />
            <Counter target={480} suffix="+" label="Referenze a Catalogo" testid="counter-referenze" />
            <Counter target={100} suffix="%" label="Selezione Halal" testid="counter-tracciabilita" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
